type RGBFormat = 'default' | 'arithmetic'

/**
 * Returns the RGBA format of a HEX color.
 * @param {string} hex - Hex color value
 * @param {number} [alpha = 1] - Alpha value ( between 0 and 1 )
 * @param {'default' | 'arithmetic'} [format = 1] - Controls the format of the r g b values
 * - arithmetic -> r, g and b will have values between 0 and 1
 * - default -> 1 (r, g and b will have values between 0 and 255)
 * @returns {[number, number, number, number]}
 */
export const hexToRgba = (hex: string, alpha = 1, format: RGBFormat = 'default'): [number, number, number, number] => {
  const percentage = format === 'arithmetic' ? 255 : 1
  const r = parseInt(hex.slice(1, 3), 16) / percentage
  const g = parseInt(hex.slice(3, 5), 16) / percentage
  const b = parseInt(hex.slice(5, 7), 16) / percentage
  return [r, g, b, alpha]
}

function hslToRGB (hslColor: [number, number, number]): [number, number, number] {
  let [h, s, l] = hslColor
  // Must be fractions of 1
  s /= 100
  l /= 100

  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l - c / 2
  let r = 0
  let g = 0
  let b = 0

  if (h >= 0 && h < 60) {
    r = c; g = x; b = 0
  } else if (h >= 60 && h < 120) {
    r = x; g = c; b = 0
  } else if (h >= 120 && h < 180) {
    r = 0; g = c; b = x
  } else if (h >= 180 && h < 240) {
    r = 0; g = x; b = c
  } else if (h >= 240 && h < 300) {
    r = x; g = 0; b = c
  } else if (h >= 300 && h < 360) {
    r = c; g = 0; b = x
  }
  r = Math.round((r + m) * 255)
  g = Math.round((g + m) * 255)
  b = Math.round((b + m) * 255)

  return [r, g, b]
}

export function hexToHSL (hexColor: string): [number, number, number] {
  const [r, g, b] = hexToRgba(hexColor, 1, 'arithmetic')

  const min = Math.min(r, g, b)
  const max = Math.max(r, g, b)
  const delta = max - min
  let h = Math.round((delta === 0
    ? 0
    : max === r
      ? ((g - b) / delta) % 6
      : max === g
        ? (b - r) / delta + 2
        : (r - g) / delta + 4) * 60)
  if (h < 0) {
    h += 360
  }

  let s = 0
  let l = 0

  l = (max + min) / 2
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))
  s = +Math.round(s * 100)
  l = +Math.round(l * 100)

  return [h, s, l]
}

/**
 * Adjusts the lightness of a HEX color by the specified amount
 * @param {string} hex
 * @param {number} amount
 */
export const adjustLightness = (hex: string, amount: number): string => {
  return '#' + hex.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2))
}

/**
 * Tint a color by a specified amount
 * @param hex original color
 * @param amount 0 means pure white, 1 means no change in the color
 * @returns the lightened color in HSL format
 */
export function lighten (hex: string, amount: number): string {
  const [h, s, l] = hexToHSL(hex)
  const newLightning = l + Math.round(((100 - l) * (1 - amount)))

  const [r, g, b] = hslToRGB([h, s, newLightning]).map(v => v.toString(16).padStart(2, '0'))

  return `#${r}${g}${b}`
}

/**
 * Dim a color by a specified amount
 * @param hex original color
 * @param amount 0 means pure black, 1 means no change in the color
 * @returns the lightened color in HSL format
 */
export function darken (hex: string, amount: number): string {
  const [h, s, l] = hexToHSL(hex)
  const newLightning = Math.round((l * amount))

  const [r, g, b] = hslToRGB([h, s, newLightning]).map(v => v.toString(16).padStart(2, '0'))

  return `#${r}${g}${b}`
}
